import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    useNavigateWithParams,
    useShowDialog,
} from '@experiences/util';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApAlertBar,
    ApButton,
    ApLink,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Controller,
    useForm,
    useWatch,
} from 'react-hook-form';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { routePaths } from '../../../common/constants/routePaths';
import { useDocumentationLinks } from '../../../common/hooks/useDocumentationLink';
import { useOrganizationName } from '../../../common/hooks/useOrganizationName';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import type {
    ByoLlmCompatibilityProbeSummary,
    ByoLlmConfiguration,
    ByoProductLlmConfiguration,
} from '../../../services/llm-gateway/LlmGateway.types';
import {
    ByoFormErrorTypes,
    LlmConnectorType,
    LlmConnectorTypesToModelFamily,
    ModelsConfigurationOption,
} from '../../../services/llm-gateway/LlmGateway.types';
import {
    getAlternativeLabelTranslationId,
    isJarvisNaturalLanguageQueryFeature,
} from '../../../services/llm-gateway/LlmGateway.utils';
import {
    addByoProductLlmConfiguration,
    updateByoProductLlmConfiguration,
    validateByoProductLlmConfiguration,
} from '../../../services/llm-gateway/LlmGatewayService';
import {
    accountGlobalId,
    accountLogicalName,
} from '../../../store/selectors';
import UiForm from '../../common/UiForm';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../../organizationsettings/AdminBreadCrumbs';
import type { AddEditConfigurationFormData } from './AddEditConfigurationViewModel';
import { useAddEditConfigurationViewModel } from './AddEditConfigurationViewModel';
import { AddEditConfigurationHeaderSection } from './subcomponents/AddEditConfigurationHeaderSection';
import { AddEditConfigurationOptionsSection } from './subcomponents/AddEditConfigurationOptionsSection';
import { AllModelsConfigurationSection } from './subcomponents/AllModelsConfigurationSection';
import { AnyModelConfigurationSection } from './subcomponents/AnyModelConfigurationSection';
import { ConnectedModelsIssuesDialogBody } from './subcomponents/ConnectedModelsIssuesDialogBody';
import { FolderDropdown } from './subcomponents/FolderDropdown';

// Extracted functions for better testability
export const processValidationResults = (
    modelsCompatibilityReports: Record<string, ByoLlmCompatibilityProbeSummary>,
    product: string,
    operationGroupName: string,
    llmConfigurations?: Record<string, Partial<ByoLlmConfiguration>>
) => {
    const isJarvisNaturalLanguageQuery = isJarvisNaturalLanguageQueryFeature(product, operationGroupName);
    const modelsWithReportedIssues = Object.entries(modelsCompatibilityReports)
        .reduce((acc, [ key, report ]) => {
            if (!report.isAvailable || !report.isCompatible) {
                const sep = key.indexOf(';');
                const desired = sep === -1 ? key : key.slice(0, sep);
                const defaultModel = sep === -1 ? undefined : key.slice(sep + 1);
                const displayKey = isJarvisNaturalLanguageQuery && defaultModel
                    ? `${desired} ${getAlternativeLabelTranslationId(product, operationGroupName, defaultModel)}`
                    : desired;
                acc[displayKey] = report;
            }
            return acc;
        }, {} as Record<string, ByoLlmCompatibilityProbeSummary>);

    // Assume models are similar if backend doesn't provide it
    Object.values(modelsCompatibilityReports).forEach(report => {
        if (report.isModelNameSimilar === undefined) {
            report.isModelNameSimilar = true;
        }
    });

    const reports = Object.values(modelsCompatibilityReports);
    const hasModelNameSimilarityIssues = reports.some(report => report.isModelNameSimilar === false);

    // Update custom field mappings for successful validations
    if (Object.keys(modelsWithReportedIssues).length === 0 && llmConfigurations) {
        Object.entries(modelsCompatibilityReports).forEach(([ model, report ]) => {
            const sep = model.indexOf(';');
            const actualModel = sep === -1 ? model : model.slice(sep + 1);
            const currentLlmConfiguration = llmConfigurations[actualModel];
            if (currentLlmConfiguration) {
                currentLlmConfiguration.customFieldMappings = report.customFieldMappings;
            }
        });
    }

    return {
        hasIssues: Object.keys(modelsWithReportedIssues).length > 0,
        modelsWithReportedIssues,
        hasModelNameSimilarityIssues,
    };
};

export const detectIncompleteLlmConfigurationErrors = (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined, useAiGateway?: boolean): string | null => {
    if (!llmConfigs || Object.values(llmConfigs).length === 0) {
        return 'CLIENT_BYO_LLM_CONFIGURE_LLM_CONFIGURATIONS_INCOMPLETE_ERROR';
    }

    for (const llmConfig of Object.values(llmConfigs)) {
        if (!llmConfig.desiredModel || !llmConfig.customerModel) {
            return 'CLIENT_BYO_LLM_CONFIGURE_LLM_CONFIGURATIONS_INCOMPLETE_ERROR';
        }

        if (useAiGateway) {
            if (!llmConfig.gatewayParameters?.apiUrl || !llmConfig.gatewayParameters.authConnectionId || !llmConfig.gatewayParameters.authConnectorType) {
                return 'CLIENT_BYO_LLM_CONFIGURE_LLM_CONFIGURATIONS_INCOMPLETE_ERROR';
            }
        } else {
            if (!llmConfig.connectionId || !llmConfig.connectorType) {
                return 'CLIENT_BYO_LLM_CONFIGURE_LLM_CONFIGURATIONS_INCOMPLETE_ERROR';
            }
        }
    }

    return null;
};

export const detectModelOverlapErrors = (
    configurationMode: string | undefined,
    modelName: string | undefined,
    initialModelName: string | undefined,
    inHouseModelNames: string[],
    customerModelNames: string[]): string | null => {
    // No overlap checks needed in replace mode
    if (configurationMode === 'replace') {
        return null;
    }

    // No model name to check
    if (!modelName) {
        return null;
    }

    // Consider initial value correct
    if (modelName === initialModelName) {
        return null;
    }

    if (inHouseModelNames.includes(modelName)) {
        return 'CLIENT_BYO_LM_CONNECTED_MODEL_NAME_OVERLAPPING_UIPATH_MODEL_ERROR';
    }

    if (customerModelNames?.includes(modelName) ?? false) {
        return 'CLIENT_BYO_LM_CONNECTED_MODEL_NAME_OVERLAPPING_BYO_MODEL_ERROR';
    }

    return null;
};

type AddEditConfigurationComponentProps = {
    readOnly?: boolean;
};

export const AddEditConfigurationComponent: React.FC<AddEditConfigurationComponentProps> = ({ readOnly }) => {
    const accountName = useSelector(accountLogicalName);
    const organizationId = useSelector(accountGlobalId);
    const {
        tenantId, folderId, id: configurationId,
    } = useParams() as { tenantId: string; folderId: string; id: string };
    const llmConfigurationsReturnUrl = useMemo(() => `${routePaths.adminAiTrustLayerLlmConfigurations}?tenantId=${tenantId}&folderId=${folderId}`, [ folderId, tenantId ]);
    const { formatMessage: translate } = useIntl();
    const theme = useTheme();
    const navigate = useNavigateWithParams();
    const createNotification = useUiSnackBar();
    const createDialog = useShowDialog();
    const organizationName = useOrganizationName();

    const {
        tenant,
        folder: routeFolder,
        editedProductLlmConfiguration,
        filteredProductConfigurations: productConfigurations,
        llmToConnectorTypeMapping,
        configuredLlms,
        availableLlmsForEditedProductLlmConfiguration,
        canUseByoAiGateway,
        enableByoForPassthrough,
        byoAiGatewayCustomConnectors,
        integrationServiceConnections,
        isViewLoading: areDependenciesLoading,
    } = useAddEditConfigurationViewModel(organizationId, tenantId, folderId, configurationId);

    const viewConfigurationUrl = useMemo(() =>
        routePaths.adminAiTrustLayerLlmConfigurationsView
            .replace(':tenantId', tenantId)
            .replace(':folderId', folderId)
            .replace(':id', configurationId),
    [ configurationId, folderId, tenantId ]);

    const editConfigurationUrl = useMemo(() =>
        routePaths.adminAiTrustLayerLlmConfigurationsEdit
            .replace(':tenantId', tenantId)
            .replace(':folderId', folderId)
            .replace(':id', configurationId),
    [ configurationId, folderId, tenantId ]);

    const editConfigurationAbsoluteUrl = useMemo(() =>
        `${globalThis.location.origin}/${accountName}/portal_${editConfigurationUrl}`,
    [ accountName, editConfigurationUrl ]);

    const addConfigurationUrl = useMemo(() =>
        routePaths.adminAiTrustLayerLlmConfigurationsAdd
            .replace(':tenantId', tenantId)
            .replace(':folderId', folderId),
    [ folderId, tenantId ]);

    const breadCrumbLinks = useMemo(() => {
        const links = [
            {
                link: routePaths.admin,
                name: organizationName,
            },
            {
                link: routePaths.adminAiTrustLayer,
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER' }),
            },
            {
                link: llmConfigurationsReturnUrl,
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_TAB' }),
            },
        ];

        if (editedProductLlmConfiguration) {
            if (readOnly) {
                links.push({
                    link: viewConfigurationUrl,
                    name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_VIEW_CONFIGURATION' }) + `: ${editedProductLlmConfiguration.name}`,
                });
            } else {
                links.push({
                    link: editConfigurationUrl,
                    name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_EDIT_CONFIGURATION' }) + `: ${editedProductLlmConfiguration.name}`,
                });
            }
        } else {
            links.push({
                link: addConfigurationUrl,
                name: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_ADD_CONFIGURATION' }),
            });
        }

        return links;
    }, [ addConfigurationUrl, editConfigurationUrl, editedProductLlmConfiguration, llmConfigurationsReturnUrl, organizationName, readOnly, translate, viewConfigurationUrl ]);
    const getLocalizedLink = useDocumentationLinks();
    const documentationLink = getLocalizedLink({ articleSlug: '/admin-guide/managing-ai-trust-layer#configuring-llms' });

    const isAllModelsConfiguration = useMemo(() => {
        if (!productConfigurations || !editedProductLlmConfiguration) {
            return undefined;
        }
        return productConfigurations[editedProductLlmConfiguration.product][editedProductLlmConfiguration.operationGroupName]
            .modelsConfigurationOption === ModelsConfigurationOption.AllModels;
    }, [ editedProductLlmConfiguration, productConfigurations ]);

    const isByoModelConfiguration = useMemo(() => {
        if (!llmToConnectorTypeMapping || !editedProductLlmConfiguration) {
            return undefined;
        }
        return !isAllModelsConfiguration && !(editedProductLlmConfiguration.llmConfigurations[0].desiredModel in llmToConnectorTypeMapping);
    }, [ editedProductLlmConfiguration, isAllModelsConfiguration, llmToConnectorTypeMapping ]);

    const isAiGatewayConfiguration = useMemo(() =>
        canUseByoAiGateway && !!editedProductLlmConfiguration?.llmConfigurations[0].gatewayParameters
    , [ canUseByoAiGateway, editedProductLlmConfiguration ]);

    const [ isFormDataLoading, setIsFormDataLoading ] = useState<boolean>();
    const [ isConfigurationTestingCommencing, setIsConfigurationTestingCommencing ] = useState<boolean>();
    const [ isConfigurationTested, setIsConfigurationTested ] = useState<boolean>();
    const [ hasModelNameSimilarityWarning, setHasModelNameSimilarityWarning ] = useState<boolean>();

    const {
        control,
        getValues,
        handleSubmit,
        resetField,
        setValue,
        setError,
        clearErrors,
        formState: {
            errors, isValid, isDirty, isSubmitting,
        },
    } = useForm<AddEditConfigurationFormData>({
        mode: 'onTouched',
        defaultValues: {
            folder: null,
            product: '',
            feature: '',
            configurationMode: '',
            useAiGateway: false,
            llmConfigurations: {},
        },
    });

    const {
        folder,
        product,
        feature,
        configurationMode,
        useAiGateway,
        llmConfigurations,
    } = useWatch({ control });

    const featureHasAllModelsConfigurationConstraint = useMemo(() => {
        if (!productConfigurations || !product || !feature) {
            return false;
        }
        return productConfigurations[product][feature].modelsConfigurationOption === ModelsConfigurationOption.AllModels;
    }, [ feature, product, productConfigurations ]);

    const featureSupportsByoModel = useMemo(() => {
        if (!productConfigurations || !product || !feature) {
            return false;
        }
        return productConfigurations[product][feature].modelsConfigurationOption === ModelsConfigurationOption.AnyModelWithOwnAdditions;
    }, [ feature, product, productConfigurations ]);

    useEffect(() => {
        if (routeFolder) {
            setValue('folder', routeFolder);
        }
    }, [ configurationId, routeFolder, setValue ]);

    useEffect(() => {
        if (routeFolder && editedProductLlmConfiguration && llmToConnectorTypeMapping && productConfigurations && integrationServiceConnections) {
            setIsFormDataLoading(true);
            setValue('folder', routeFolder);

            if (configurationId) {
                setValue('product', editedProductLlmConfiguration.product);
                setValue('feature', editedProductLlmConfiguration.operationGroupName);
                setValue('configurationMode', isByoModelConfiguration ? 'add' : 'replace');
                setValue('useAiGateway', isAiGatewayConfiguration ?? false);

                setValue('llmConfigurations', editedProductLlmConfiguration.llmConfigurations.reduce((acc, llmConfig) => {
                    if (isAiGatewayConfiguration && llmConfig.gatewayParameters) {
                        llmConfig.gatewayParameters.authConnectorType =
                            integrationServiceConnections.find(connection => connection.id === llmConfig.gatewayParameters?.authConnectionId)
                                ?.connector?.key ?? '';
                    }

                    const mapKey = llmConfig.defaultModel ?? llmConfig.desiredModel;
                    acc[mapKey] = llmConfig;
                    return acc;
                }, {} as Record<string, Partial<ByoLlmConfiguration>>));
            }

            setIsFormDataLoading(false);
        }
    }, [
        areDependenciesLoading,
        canUseByoAiGateway,
        configurationId,
        editedProductLlmConfiguration,
        integrationServiceConnections,
        isAiGatewayConfiguration,
        isAllModelsConfiguration,
        isByoModelConfiguration,
        llmToConnectorTypeMapping,
        productConfigurations,
        routeFolder,
        setValue,
    ]);

    const getCurrentByoProductLlmConfigurationFromValues = useCallback((): Partial<ByoProductLlmConfiguration> => {
        const values = getValues();

        const configuration = {
            id: configurationId,
            organizationId,
            tenantId,
            folderId: values.folder?.Key,
            name: 'placeholder-name', // Placeholder name, will be set later
            product: values.product,
            operationGroupName: values.feature,
            llmConfigurations: Object.values(values.llmConfigurations).map(llmConfig => ({
                id: llmConfig.id,
                organizationId: llmConfig.organizationId ?? organizationId,
                tenantId: llmConfig.tenantId ?? tenantId,
                connectionId: llmConfig.connectionId,
                connectorType: llmConfig.connectorType,
                apiFlavor: llmConfig.apiFlavor,
                defaultModel: llmConfig.defaultModel ?? llmConfig.desiredModel,
                desiredModel: llmConfig.desiredModel,
                customerModel: llmConfig.customerModel,
                gatewayParameters: llmConfig.gatewayParameters,
                customFieldMappings: llmConfig.customFieldMappings,
            } as ByoLlmConfiguration)),
            enabled: true,
        };

        if (values.useAiGateway) {
            configuration.llmConfigurations.forEach(llmConfig => {
                if (values.configurationMode === 'replace' && llmConfig.gatewayParameters) {
                    if (!llmConfig.gatewayParameters.vendorApiType) {
                        llmConfig.gatewayParameters.vendorApiType =
                            LlmConnectorTypesToModelFamily[llmToConnectorTypeMapping[llmConfig.desiredModel ?? ''].at(0) ?? LlmConnectorType.AzureOpenAi];
                    }
                }
            });
        }

        return configuration;
    }, [ configurationId, getValues, llmToConnectorTypeMapping, organizationId, tenantId ]);

    const validateConfiguration = useCallback(async (request: Partial<ByoProductLlmConfiguration>) => {
        try {
            setIsConfigurationTestingCommencing(true);
            const modelsCompatibilityReports = await validateByoProductLlmConfiguration(
                organizationId,
                tenantId,
                request,
                configurationMode
            );

            const result = processValidationResults(
                modelsCompatibilityReports,
                request.product ?? '',
                request.operationGroupName ?? '',
                llmConfigurations as Record<string, Partial<ByoLlmConfiguration>> | undefined
            );

            // Set model name similarity warning state
            setHasModelNameSimilarityWarning(result.hasModelNameSimilarityIssues);

            if (result.hasIssues) {
                await createDialog({
                    title: translate({ id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_INVALID_CONFIGURATION_CONNECTED_MODELS_ISSUES_TITLE' }),
                    body: (
                        <ConnectedModelsIssuesDialogBody
                            modelsWithReportedIssues={result.modelsWithReportedIssues} />
                    ),
                    icon: 'error',
                    hideActions: true,
                });
            } else {
                if (llmConfigurations) {
                    setValue('llmConfigurations', llmConfigurations as Record<string, Partial<ByoLlmConfiguration>>); // Update with potential custom field mappings
                }
                setIsConfigurationTested(true);
            }
        } catch (error: any) {
            createNotification(translate(
                { id: 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_VALIDATE' },
                { error: error?.response?.data?.message ?? error?.message }
            ), 'error');
        } finally {
            setIsConfigurationTestingCommencing(false);
        }
    }, [ createDialog, createNotification, configurationMode, llmConfigurations, organizationId, setValue, tenantId, translate ]);

    const modifyConfiguration = useCallback(async (request: Partial<ByoProductLlmConfiguration>) => {
        try {
            if (configurationId) {
                await updateByoProductLlmConfiguration(organizationId, tenantId, configurationId, request);
            } else {
                await addByoProductLlmConfiguration(organizationId, tenantId, request);
            }
            navigate(llmConfigurationsReturnUrl);
        } catch (error: any) {
            const stringIdentifier = configurationId
                ? 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_EDIT'
                : 'CLIENT_AI_TRUST_LAYER_LLM_CONFIGURATIONS_FAILED_TO_ADD';
            createNotification(translate({ id: stringIdentifier }, { error: error?.response?.data?.message ?? error?.message }), 'error');
        }
    }, [ configurationId, createNotification, llmConfigurationsReturnUrl, navigate, organizationId, tenantId, translate ]);

    const onClose = useCallback(async () => {
        navigate(llmConfigurationsReturnUrl);
    }, [ llmConfigurationsReturnUrl, navigate ]);

    const onTest = useCallback(async () => {
        const request = getCurrentByoProductLlmConfigurationFromValues();

        if (!isValid) {
            return;
        }

        // Placeholder name
        request.name = `validation-${request.operationGroupName}-${Date.now()}`;
        await validateConfiguration(request);
    }, [ getCurrentByoProductLlmConfigurationFromValues, isValid, validateConfiguration ]);

    const onSubmit = useCallback(async () => {
        const request = getCurrentByoProductLlmConfigurationFromValues();

        if (!isValid || !isConfigurationTested) {
            return;
        }

        request.name = `${request.operationGroupName}-${Date.now()}`;
        await modifyConfiguration(request);
    }, [ getCurrentByoProductLlmConfigurationFromValues, isConfigurationTested, isValid, modifyConfiguration ]);

    const validateLlmConfigurationsRequiredFields = useCallback(async (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => {
        const errorId = detectIncompleteLlmConfigurationErrors(llmConfigs, useAiGateway);

        if (errorId) {
            return translate({ id: errorId });
        }

        // Manually clear errors generated outside controlled validation
        clearErrors('llmConfigurations');
        return true;
    }, [ clearErrors, translate, useAiGateway ]);

    const validateLlmConfigurationsModelNameOverlap = useCallback(async (llmConfigs: Record<string, Partial<ByoLlmConfiguration>> | undefined) => {
        if (!llmConfigs || Object.values(llmConfigs).length === 0) {
            return true;
        }

        const modelName = Object.values(llmConfigs)[0].desiredModel;
        const initialModelName = editedProductLlmConfiguration?.llmConfigurations[0]?.desiredModel;
        const inHouseModelNames = Object.keys(llmToConnectorTypeMapping);
        const customerModelNames = configuredLlms?.[product ?? '']?.[feature ?? ''] ?? [];

        const errorId = detectModelOverlapErrors(configurationMode, modelName, initialModelName, inHouseModelNames, customerModelNames);

        if (errorId) {
            return translate({ id: errorId });
        }

        // Manually clear errors generated outside controlled validation
        clearErrors('llmConfigurations');
        return true;
    }, [ clearErrors, configurationMode, configuredLlms, editedProductLlmConfiguration?.llmConfigurations, feature, llmToConnectorTypeMapping, product, translate ]);

    return (
        <UiPageContainer
            loading={areDependenciesLoading || isFormDataLoading}
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadCrumbLinks} />}
            position='left'>
            <UiForm
                onSubmit={handleSubmit(onSubmit)}
                addScrollPadding
                actions={
                    <UiStack
                        direction='column'
                        gap={SpacingToken.S}>
                        {!readOnly && (
                            <>
                                <UiStack
                                    direction="row"
                                    align='center'
                                    gap={SpacingToken.XL}>
                                    <UiText
                                        variant={FontVariantToken.fontSizeM}
                                    >
                                        <FormattedMessage
                                            id='CLIENT_BYO_LLM_CONFIGURE_TEST_DESCRIPTION'
                                            values={{
                                                learnMore: (msg: React.ReactNode[]) =>
                                                    (
                                                        <ApLink
                                                            href={documentationLink}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                        >
                                                            {msg}
                                                        </ApLink>
                                                    ),
                                            }}
                                        />
                                    </UiText>
                                    <ApButton
                                        label={translate({ id: 'CLIENT_BYO_LLM_CONFIGURE_TEST' })}
                                        variant='primary'
                                        disabled={!isDirty || !isValid || Object.keys(errors).length > 0}
                                        loading={isConfigurationTestingCommencing}
                                        type="button"
                                        onClick={onTest}
                                        data-cy="add-edit-llm-configuration-test-button" />
                                </UiStack>
                                <Divider style={{ borderColor: theme.palette.semantic.colorBorderDeEmp }} />
                            </>
                        )}
                        <UiStack
                            direction="row"
                            justify="end"
                            gap={SpacingToken.S}>
                            <ApButton
                                label={translate({ id: readOnly ? 'CLIENT_BACK' : 'CLIENT_CANCEL' })}
                                variant='secondary'
                                onClick={() => onClose()}
                                data-cy="add-edit-llm-configuration-cancel-button" />
                            {!readOnly && (
                                <ApButton
                                    label={translate({ id: 'CLIENT_SAVE' })}
                                    variant='primary'
                                    disabled={!isConfigurationTested}
                                    loading={isSubmitting}
                                    type="submit"
                                    data-cy="add-edit-llm-configuration-save-button" />
                            )}
                        </UiStack>
                    </UiStack>
                }>
                <UiStack
                    h='100%'
                    direction='column'
                    mt={SpacingToken.Base}
                    gap={SpacingToken.L}>
                    <UiStack
                        direction='column'
                        gap={SpacingToken.L}>
                        <AddEditConfigurationHeaderSection
                            control={control}
                            resetField={resetField}
                            setValue={setValue}
                            setError={setError}
                            tenantName={tenant?.name}
                            product={product}
                            notifyFieldChanges={() => setIsConfigurationTested(false)}
                            productConfigurations={productConfigurations ?? {}}
                            replaceableLlms={availableLlmsForEditedProductLlmConfiguration ?? {}}
                            disabled={readOnly}
                        />
                        {product && feature && (
                            <>
                                {readOnly ? (
                                    <ApAlertBar
                                        status="warning"
                                        cancelable={false}
                                    >
                                        <FormattedMessage
                                            id='CLIENT_BYO_LLM_CONFIGURE_UPGRADE_REQUIRED_DESCRIPTION'
                                            values={{
                                                upgrade: (msg: React.ReactNode[]) =>
                                                    (
                                                        <ApLink
                                                            href={editConfigurationAbsoluteUrl}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                        >
                                                            {msg}
                                                        </ApLink>
                                                    ),
                                            }}
                                        />
                                    </ApAlertBar>
                                ) : (
                                    <ApAlertBar
                                        status="info"
                                        cancelable={false}
                                    >
                                        <FormattedMessage
                                            id='CLIENT_BYO_LLM_CONFIGURE_FEATURE_DESCRIPTION'
                                            values={{
                                                documentationLink: (msg: React.ReactNode[]) =>
                                                    (
                                                        <ApLink
                                                            href={documentationLink}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                        >
                                                            {msg}
                                                        </ApLink>
                                                    ),
                                            }}
                                        />
                                    </ApAlertBar>
                                )}
                                {hasModelNameSimilarityWarning && configurationMode === 'replace' && (
                                    <ApAlertBar
                                        status="warning"
                                        cancelable={false}
                                    >
                                        <FormattedMessage id="CLIENT_BYO_LLM_MODEL_SIMILARITY_WARNING" />
                                    </ApAlertBar>
                                )}
                                <AddEditConfigurationOptionsSection
                                    control={control}
                                    resetField={resetField}
                                    setValue={setValue}
                                    configurationMode={configurationMode}
                                    feature={feature}
                                    featureHasAllModelsConfigurationConstraint={featureHasAllModelsConfigurationConstraint}
                                    featureSupportsByoModel={featureSupportsByoModel}
                                    canUseByoAiGateway={canUseByoAiGateway}
                                    notifyFieldChanges={() => setIsConfigurationTested(false)}
                                    replaceableLlms={availableLlmsForEditedProductLlmConfiguration?.[product][feature] ?? []}
                                    disabled={readOnly}
                                />
                                <Controller
                                    name="folder"
                                    control={control}
                                    rules={{ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }}
                                    render={({ field }) => (
                                        <FolderDropdown
                                            tenant={tenant}
                                            value={field.value ?? undefined}
                                            required
                                            disabled={readOnly}
                                            disableAutoSelectFirstOption
                                            onChange={(newValue) => {
                                                field.onChange(newValue);

                                                if (featureHasAllModelsConfigurationConstraint) {
                                                    setValue('llmConfigurations', availableLlmsForEditedProductLlmConfiguration?.[product][feature]
                                                        .reduce((acc, llm) => {
                                                            acc[llm] = {
                                                                desiredModel: llm,
                                                                defaultModel: llm,
                                                            };
                                                            return acc;
                                                        }, {} as Record<string, Partial<ByoLlmConfiguration>>) ?? {});
                                                } else if (configurationMode === 'replace') {
                                                    resetField('llmConfigurations');
                                                } else {
                                                    // Add entry for empty alias
                                                    setValue('llmConfigurations', { '': {} });
                                                }

                                                setIsConfigurationTested(false);
                                            }}
                                            labelTooltip={{
                                                text: translate({ id: 'CLIENT_BYO_LLM_FOLDER_TOOLTIP' }),
                                                placement: 'right',
                                            }} />
                                    )}
                                />
                                <Controller
                                    name="llmConfigurations"
                                    control={control}
                                    rules={{
                                        required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }),
                                        validate: {
                                            [ByoFormErrorTypes.RequiredField]: validateLlmConfigurationsRequiredFields,
                                            [ByoFormErrorTypes.ModelNameOverlap]: validateLlmConfigurationsModelNameOverlap,
                                        },
                                    }}
                                    render={({
                                        field, fieldState,
                                    }) =>
                                        featureHasAllModelsConfigurationConstraint
                                            ? <AllModelsConfigurationSection
                                                organizationId={organizationId}
                                                tenantId={tenantId}
                                                folderKey={folder?.Key}
                                                useAiGateway={useAiGateway}
                                                enableByoForPassthrough={enableByoForPassthrough}
                                                llmConfigurationsField={field}
                                                notifyFieldChanges={() => setIsConfigurationTested(false)}
                                                llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                                                byoAiGatewayCustomConnectors={byoAiGatewayCustomConnectors}
                                                product={product}
                                                feature={feature}
                                                productConfigurations={productConfigurations}
                                                readOnly={readOnly}
                                            />
                                            : <AnyModelConfigurationSection
                                                organizationId={organizationId}
                                                tenantId={tenantId}
                                                folderKey={folder?.Key}
                                                configurationMode={configurationMode}
                                                useAiGateway={useAiGateway}
                                                enableByoForPassthrough={enableByoForPassthrough}
                                                llmConfigurationsField={field}
                                                notifyFieldChanges={() => setIsConfigurationTested(false)}
                                                llmToConnectorTypeMapping={llmToConnectorTypeMapping}
                                                replaceableLlms={availableLlmsForEditedProductLlmConfiguration?.[product][feature] ?? []}
                                                byoAiGatewayCustomConnectors={byoAiGatewayCustomConnectors}
                                                product={product}
                                                feature={feature}
                                                productConfigurations={productConfigurations}
                                                error={fieldState.error}
                                                readOnly={readOnly}
                                            />}
                                />
                            </>
                        )}
                    </UiStack>
                </UiStack>
            </UiForm>
        </UiPageContainer>
    );
};
